"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Send, Upload, Users, History, AlertCircle, CheckCircle2, Clock, Plus } from "lucide-react";
import { SinglePayoutModal } from "@/components/modals/single-payout-modal";
import { BulkPayoutModal } from "@/components/modals/bulk-payout-modal";
import { AddBeneficiaryModal } from "@/components/modals/add-beneficiary-modal";
import { SettlementModals } from "@/components/modals/settlement-modals";

export default function PayoutsPage() {
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payouts</h1>
          <p className="text-muted-foreground mt-1">Manage single, bulk payouts, and your beneficiaries.</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkPayoutModal>
            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" /> Bulk Upload
            </Button>
          </BulkPayoutModal>
          <SinglePayoutModal>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="mr-2 h-4 w-4" /> Send Payout
            </Button>
          </SinglePayoutModal>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Today</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦4,250,000</div>
            <p className="text-xs text-muted-foreground mt-1">142 transactions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">135</div>
            <p className="text-xs text-emerald-500 mt-1">95.0% success rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">5</div>
            <p className="text-xs text-amber-500 mt-1">Processing...</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2</div>
            <p className="text-xs text-destructive mt-1">Requires attention</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="history" className="w-full">
        <TabsList className="flex w-full overflow-x-auto justify-start md:grid md:grid-cols-4 lg:w-[600px] bg-muted/50 p-1 no-scrollbar">
          <TabsTrigger value="history" className="shrink-0 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">History</TabsTrigger>
          <TabsTrigger value="approvals" className="shrink-0 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">
            Approvals <Badge variant="destructive" className="ml-2 h-4 w-4 p-0 flex items-center justify-center text-[10px] rounded-full">3</Badge>
          </TabsTrigger>
          <TabsTrigger value="beneficiaries" className="shrink-0 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">Beneficiaries</TabsTrigger>
          <TabsTrigger value="settlements" className="shrink-0 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">Settlements</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Payout History</CardTitle>
              <CardDescription>Recent transfers from your wallet.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { recipient: "Tech Corp Ltd", bank: "GTBank", amount: "₦450,000.00", date: "Today, 10:24 AM", ref: "TRX-10928374", status: "Success", type: "success" },
                    { recipient: "John Smith", bank: "Access Bank", amount: "₦12,500.00", date: "Today, 09:12 AM", ref: "TRX-10928373", status: "Success", type: "success" },
                    { recipient: "Jane Doe", bank: "Zenith Bank", amount: "₦85,000.00", date: "Yesterday, 14:30 PM", ref: "TRX-10928372", status: "Pending", type: "warning" },
                    { recipient: "Acme Logistics", bank: "UBA", amount: "₦120,000.00", date: "Yesterday, 08:15 AM", ref: "TRX-10928371", status: "Failed", type: "destructive" },
                  ].map((tx) => (
                    <TableRow key={tx.ref}>
                      <TableCell>
                        <div className="font-medium">{tx.recipient}</div>
                        <div className="text-xs text-muted-foreground">{tx.bank}</div>
                      </TableCell>
                      <TableCell className="font-medium">{tx.amount}</TableCell>
                      <TableCell className="text-muted-foreground">{tx.date}</TableCell>
                      <TableCell className="text-xs font-mono">{tx.ref}</TableCell>
                      <TableCell>
                        <Badge variant={tx.type as any} className={
                          tx.type === "success" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400" :
                          tx.type === "warning" ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400" :
                          "bg-destructive/10 text-destructive dark:bg-destructive/20"
                        }>
                          {tx.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-primary/10">View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="beneficiaries" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row justify-between items-start">
              <div>
                <CardTitle>Saved Beneficiaries</CardTitle>
                <CardDescription>Manage your frequently used payout accounts.</CardDescription>
              </div>
              <AddBeneficiaryModal>
                <Button size="sm"><Plus className="h-4 w-4 mr-2"/> Add New</Button>
              </AddBeneficiaryModal>
            </CardHeader>
            <CardContent>
               <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Account Number</TableHead>
                    <TableHead>Last Paid</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { name: "John Smith", bank: "Access Bank", acc: "0123456789", lastPaid: "Today" },
                    { name: "Tech Corp Ltd", bank: "GTBank", acc: "0987654321", lastPaid: "Yesterday" },
                    { name: "Jane Doe", bank: "Zenith Bank", acc: "1122334455", lastPaid: "2 weeks ago" },
                  ].map((ben) => (
                    <TableRow key={ben.acc}>
                      <TableCell className="font-medium">{ben.name}</TableCell>
                      <TableCell>{ben.bank}</TableCell>
                      <TableCell className="font-mono text-sm">{ben.acc}</TableCell>
                      <TableCell className="text-muted-foreground">{ben.lastPaid}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" className="mr-2">Send Money</Button>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10">Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settlements" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Settlement Settings</CardTitle>
                  <CardDescription>Configure how and where your earnings are settled.</CardDescription>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Auto-Settlement Active</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Settlement Frequency</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                      <option>Daily (Next Day)</option>
                      <option>Weekly (Every Monday)</option>
                      <option>Manual (On Request)</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground italic">Daily settlements are processed at 12:00 AM UTC.</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Destination Account</Label>
                    <div className="p-3 border rounded-lg bg-muted/20 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center">
                          <Landmark className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-bold">Zenith Bank</p>
                          <p className="text-xs text-muted-foreground">1029384756 • PayTaara Tech</p>
                        </div>
                      </div>
                      <SettlementModals type="account">
                        <Button variant="ghost" size="sm" className="text-xs text-primary">Change</Button>
                      </SettlementModals>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-primary/5 border border-primary/10 space-y-4">
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-primary" />
                    Settlement Logic
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-xs text-muted-foreground">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 shrink-0" />
                      Earnings from Day T are settled on Day T+1.
                    </li>
                    <li className="flex items-start gap-2 text-xs text-muted-foreground">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 shrink-0" />
                      Minimum settlement amount is ₦1,000.00.
                    </li>
                    <li className="flex items-start gap-2 text-xs text-muted-foreground">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 shrink-0" />
                      Settlement fees: 0.5% (capped at ₦2,000).
                    </li>
                  </ul>
                  <SettlementModals type="logic">
                    <Button className="w-full bg-primary/10 text-primary hover:bg-primary/20 border-none shadow-none text-xs">Update Preferences</Button>
                  </SettlementModals>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Settlement History</CardTitle>
              <CardDescription>List of all funds settled to your bank account.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Amount (Gross)</TableHead>
                    <TableHead>Fees</TableHead>
                    <TableHead>Net Settlement</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { ref: "SETL-89021", gross: "₦1,240,000.00", fees: "₦2,000.00", net: "₦1,238,000.00", date: "Oct 24, 2023", status: "Paid" },
                    { ref: "SETL-89020", gross: "₦850,500.00", fees: "₦2,000.00", net: "₦848,500.00", date: "Oct 23, 2023", status: "Paid" },
                    { ref: "SETL-89019", gross: "₦420,000.00", fees: "₦2,000.00", net: "₦418,000.00", date: "Oct 22, 2023", status: "Paid" },
                    { ref: "SETL-89018", gross: "₦2,100,000.00", fees: "₦2,000.00", net: "₦2,098,000.00", date: "Oct 21, 2023", status: "Processing" },
                  ].map((s) => (
                    <TableRow key={s.ref}>
                      <TableCell className="font-mono text-xs">{s.ref}</TableCell>
                      <TableCell className="font-medium">{s.gross}</TableCell>
                      <TableCell className="text-destructive text-xs">{s.fees}</TableCell>
                      <TableCell className="font-bold text-emerald-600">{s.net}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{s.date}</TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "text-[10px] font-bold uppercase",
                          s.status === "Paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        )}>
                          {s.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="approvals" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pending Approvals</CardTitle>
              <CardDescription>Review and authorize payouts that exceed your configured threshold.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { user: "jane.smith@paytaara.com", recipient: "Global Trade Inc", bank: "Access Bank", amount: "₦2,500,000.00", date: "2 hrs ago" },
                    { user: "jane.smith@paytaara.com", recipient: "Salary Disburse", bank: "Zenith Bank", amount: "₦14,200,000.00", date: "4 hrs ago" },
                    { user: "api-key (Production)", recipient: "Vendor Payment", bank: "GTBank", amount: "₦1,150,000.00", date: "Yesterday" },
                  ].map((req, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-xs">{req.user}</TableCell>
                      <TableCell>
                        <div className="font-medium">{req.recipient}</div>
                        <div className="text-xs text-muted-foreground">{req.bank}</div>
                      </TableCell>
                      <TableCell className="font-bold">{req.amount}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{req.date}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" className="mr-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700">Approve</Button>
                        <Button variant="outline" size="sm" className="border-destructive/20 text-destructive hover:bg-destructive/10 hover:text-destructive">Reject</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

import { Landmark } from "lucide-react";
import { cn } from "@/lib/utils";
